import React from "react";
import {
    Badge, Box, Button, Flex, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import { directionLabel } from "./seedPairs";

/**
 * Formats a timestamp for the "last change" column.
 * @param {string|null} stamp - ISO date from the API
 * @returns {string}
 */
function formatStamp(stamp) {
    return stamp ? new Date(stamp).toLocaleString("fr-FR") : "—";
}

/**
 * Renders one rate row.
 * @param {Object} props - the rate and the edit handler
 * @returns {React.ReactElement}
 */
function RateRow({ rate, onEdit }) {
    const valueColor = useColorModeValue("secondaryGray.900", "white");
    const mutedColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    return (
        <Tr>
            <Td fontWeight="700" color={valueColor}>
                {directionLabel(rate.baseCurrency, rate.quoteCurrency)}
            </Td>
            <Td color={valueColor}>{rate.rate}</Td>
            <Td color={mutedColor}>{rate.marginPercent} %</Td>
            <Td>
                <Badge colorScheme={rate.active ? "green" : "red"} borderRadius="6px" px="8px">
                    {rate.active ? "Actif" : "Inactif"}
                </Badge>
            </Td>
            <Td fontSize="xs" color={mutedColor}>{formatStamp(rate.modifiedAt)}</Td>
            <Td>
                <Button size="xs" variant="outline" onClick={() => onEdit(rate)}>Modifier</Button>
            </Td>
        </Tr>
    );
}

/**
 * Renders the loading, error and empty states of the table.
 * @param {Object} props - loading flag, error payload and emptiness flag
 * @returns {React.ReactElement|null}
 */
function TableState({ loading, error, isEmpty }) {
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (loading) return <Flex justify="center" py="30px"><Spinner color="brand.500" /></Flex>;
    if (error) {
        return (
            <Text fontSize="sm" color="red.400" py="16px">
                {typeof error === "string" ? error : "Impossible de charger les taux de change"}
            </Text>
        );
    }
    if (isEmpty) {
        return (
            <Text fontSize="sm" color={hintColor} py="16px">
                Aucun taux configuré. Utilise l'initialisation rapide ci-dessus, ou ajoute une
                direction à la main.
            </Text>
        );
    }
    return null;
}

/**
 * Table of every configured direction. Each direction is its own row on
 * purpose: exchange is not symmetric, so EUR → MGA and MGA → EUR are edited
 * independently.
 * @param {Object} props - rates, loading, error and the edit handler
 * @returns {React.ReactElement}
 */
function RatesTable({ rates, loading, error, onEdit }) {
    const cardBg = useColorModeValue("white", "navy.800");
    const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const isEmpty = !loading && !error && rates.length === 0;

    return (
        <Box bg={cardBg} borderRadius="16px" p="20px" mb="20px" overflowX="auto">
            <TableState loading={loading} error={error} isEmpty={isEmpty} />
            {!loading && !error && rates.length > 0 && (
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr>
                            {["Direction", "Taux", "Marge", "Statut", "Dernière modification", ""].map((head) => (
                                <Th key={head} color={headColor} fontSize="xs">{head}</Th>
                            ))}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {rates.map((rate) => <RateRow key={rate.id} rate={rate} onEdit={onEdit} />)}
                    </Tbody>
                </Table>
            )}
        </Box>
    );
}

export default RatesTable;
